/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, GetNodeGroupByIdResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { scheduleInvalidate } from '@/hooks/query/invalidationScheduler';
import { useSubscribeToEvents } from '@/hooks/useSubscribeToEvents';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UseNodeGroupByIdQueryProps = {
  nodeGroupId: string;
};

/**
 * Custom hook that fetches and subscribes to node group data updates.
 * Combines React Query for data fetching with WebSocket subscriptions for real-time updates.
 *
 * @param props.nodeGroupId - Unique identifier of the node group to fetch
 * @returns React Query result containing node group data and query state
 */
export function useNodeGroupByIdQuery({ nodeGroupId }: UseNodeGroupByIdQueryProps) {
  const queryClient = useQueryClient();
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  useSubscribeToEvents(['nodeGroup:updated', 'nodeGroup:deleted'], () => {
    scheduleInvalidate(queryClient, ['nodeGroups', selectedTenantId]);
  });

  return useQuery<GetNodeGroupByIdResponse, ApiError>({
    queryKey: ['nodeGroups', selectedTenantId, nodeGroupId],
    queryFn: () => {
      return mgmtPlaneAPIClient.nodeGroups.getNodeGroupById({
        nodeGroupId,
        tenantId: selectedTenantId!,
      });
    },
    enabled: !!selectedTenantId && !!nodeGroupId,
  });
}
